import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { FolderActionsContext, Mode, OrderBy, Sort, SortBy } from './context';
import { graphql, RelaySafeProvider, useSafeRelay } from 'utils/graphClient';
import { GenericErrorFallback } from 'components/GenericErrorBoundary';
import { CenteredSpinner } from 'components/CenteredSpinner';
import { useLocalStorage } from 'hooks/useBrowserStorage';
import { SPACE_FILES_PARAM, SPACE_PARAM } from 'utils/routes';
import { provider_node_Query } from './__generated__/provider_node_Query.graphql';
import { ConnectionHandler } from 'relay-runtime';
import useSafeParam from 'utils/routes/useSafeParam';

const nodeQuery = graphql`
  query provider_node_Query($id: ID!) {
    node(id: $id) {
      ... on Space {
        __typename
        id
        folder {
          id
        }
      }
    }
  }
`;

export const useFolderActions = () => useContext(FolderActionsContext);

const SPACE_FILES_MODE_STORAGE_KEY = 'spaceFilesMode';
const DEFAULT_MODE = Mode.TABLE;
const DEFAULT_SORT = Sort.NEWEST_TO_OLDEST;

const UnsafeFolderActionsProvider: FC<PropsWithChildren> = ({ children }) => {
  const spaceId = useSafeParam(SPACE_PARAM);

  const { useLazyLoadQuery } = useSafeRelay();
  const { node } = useLazyLoadQuery<provider_node_Query>(nodeQuery, {
    id: spaceId,
  });

  const [mode, setMode] = useLocalStorage<Mode>(
    SPACE_FILES_MODE_STORAGE_KEY,
    DEFAULT_MODE,
    'device',
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);

  const rootFolderId = node?.__typename === 'Space' ? node.folder.id : '';
  const hasFolderParam = searchParams.has(SPACE_FILES_PARAM);
  useEffect(() => {
    if (rootFolderId && !hasFolderParam) {
      const newParams = new URLSearchParams(searchParams);

      newParams.set(SPACE_FILES_PARAM, rootFolderId);

      setSearchParams(newParams);
    }
  }, [searchParams, rootFolderId, hasFolderParam]);

  const getOrderBy = useCallback((): OrderBy => {
    switch (sort) {
      case Sort.OLDEST_TO_NEWEST:
        return SortBy.OLDEST_TO_NEWEST;
      case Sort.NEWEST_TO_OLDEST:
        return SortBy.NEWEST_TO_OLDEST;
      case Sort.ALPHABETICAL_AZ:
        return SortBy.ALPHABETICAL_AZ;
      case Sort.ALPHABETICAL_ZA:
        return SortBy.ALPHABETICAL_ZA;
      default:
        return SortBy.NEWEST_TO_OLDEST;
    }
  }, [sort]);

  const getFolderConnections = useCallback((folderId: string) => {
    return [
      ConnectionHandler.getConnectionID(
        folderId,
        'FolderTable_folder_folders',
        {
          orderBy: getOrderBy(),
        },
      ),
      ConnectionHandler.getConnectionID(folderId, 'FolderGrid_folder_folders', {
        orderBy: getOrderBy(),
      }),
    ];
  }, []);

  const getFolderItemConnections = useCallback((folderId: string) => {
    return [
      ConnectionHandler.getConnectionID(folderId, 'FolderTable_folder_items', {
        orderBy: getOrderBy(),
      }),
      ConnectionHandler.getConnectionID(folderId, 'FolderGrid_folder_items', {
        orderBy: getOrderBy(),
      }),
    ];
  }, []);

  return (
    <FolderActionsContext.Provider
      value={{
        rootFolderId,

        searchQuery,
        setSearchQuery,

        mode,
        setMode,

        sort,
        setSort,
        getOrderBy,

        getFolderConnections,
        getFolderItemConnections,
      }}
    >
      {children}
    </FolderActionsContext.Provider>
  );
};

export const FolderActionsProvider: FC<PropsWithChildren> = ({ children }) => {
  return (
    <RelaySafeProvider
      ErrorComponent={GenericErrorFallback}
      LoadingComponent={CenteredSpinner}
    >
      <UnsafeFolderActionsProvider>{children}</UnsafeFolderActionsProvider>
    </RelaySafeProvider>
  );
};
